import {assertAndContinue} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {findColumnByDisplayName} from 'data-frames/util/findColumn';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';

type VisualizationParameters = AnalyticsVisualization['parameters'];

const COLUMN_NAME_PROPS = [
  'xAxisColumnName',
  'yAxisColumnName',
  'rowIdColumnName',
  'measureColumnName',
  'metricColumnName',
  'seriesColumnName',
  'sliceColumnName',
];

/**
 * Convert display name-based visualization parameters to use system names for all fields that refer to a
 * column name in the data frame.
 *
 * This is used in legacy insights code (`AnalyticsBlock`) as we store display names in those visualization
 * parameters and we don't have an easy migration path to get them switched to using system names. Other users
 * of `PresentationalUniversalBlockViewer` will use system names.
 */
export default function convertDisplayNameBasedConfigs(
  dataFrame: DataFrame,
  parameters: VisualizationParameters
): {parameters: VisualizationParameters; missingColumns: Array<string>} {
  const missingColumns: Array<string> = [];
  const transformedParameters = {
    ...parameters,
  };

  // In order to protect against accidental ommissions of future `ColumnName` related fields, we assert that
  // all parameter fields ending with `'ColumnName'` are handled in this function before continuing, since
  // there is not way to do this using the type system.
  Object.keys(parameters).forEach((prop) => {
    if (prop.toLowerCase().includes('columnname') && !COLUMN_NAME_PROPS.includes(prop)) {
      assertAndContinue(
        false,
        `AnalyticsVisualizationParameters contains unhandled column name property ${prop}`
      );
    }
  });

  COLUMN_NAME_PROPS.forEach((prop) => {
    if (parameters.hasOwnProperty(prop)) {
      // @ts-expect-error
      const displayName = transformedParameters[prop];

      if (displayName == null) {
        return;
      }

      const column = findColumnByDisplayName(dataFrame, displayName);

      if (column == null) {
        missingColumns.push(displayName);
      } else {
        // @ts-expect-error
        transformedParameters[prop] = column.systemName;
      }
    }
  });

  return {
    parameters: transformedParameters,
    missingColumns,
  };
}
